import React, { FormEvent, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import clinicalSession from '../services/clinicalSession';
import { getWorklistState, saveWorklistState } from '../stores/worklistState';
import clinicalApi, {
  Filters,
  safeReturnPath,
  Study,
  StudyPage,
  viewerURL,
} from '../services/clinicalApi';
import {
  ErrorMessage,
  errorText,
  HistoryView,
  Pagination,
  ReportView,
  statusText,
} from './RecordDetails';
import './clinical.css';

export function SessionGate({ children }: { children: React.ReactNode }) {
  const session = useSyncExternalStore(clinicalSession.subscribe, clinicalSession.getSnapshot);
  const location = useLocation();
  return session ? (
    <>{children}</>
  ) : (
    <Navigate
      to="/clinical/login"
      replace
      state={{ returnTo: `${location.pathname}${location.search}` }}
    />
  );
}
export function LoginPage() {
  const session = useSyncExternalStore(clinicalSession.subscribe, clinicalSession.getSnapshot);
  const navigate = useNavigate();
  const location = useLocation();
  const request = useRef<AbortController | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => () => request.current?.abort(), []);
  const returnTo = safeReturnPath(location.state?.returnTo);
  if (session) {
    return (
      <Navigate
        to={returnTo}
        replace
      />
    );
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setBusy(true);
    setError('');
    try {
      await clinicalSession.login(username.trim(), password, controller.signal);
      setPassword('');
      navigate(returnTo, { replace: true });
    } catch (error) {
      if (!controller.signal.aborted) {
        setError(errorText(error));
      }
    }
    if (!controller.signal.aborted) {
      setBusy(false);
      setPassword('');
    }
  }
  return (
    <main className="clinical-login">
      <form
        onSubmit={submit}
        data-cy="clinical-login-form"
      >
        <h1>临床影像工作站</h1>
        <p>使用医生账号登录，查阅影像、报告和历史检查</p>
        <label>
          医生账号
          <input
            name="username"
            autoComplete="username"
            required
            value={username}
            onChange={event => setUsername(event.target.value)}
            data-cy="clinical-username"
          />
        </label>
        <label>
          密码
          <input
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={event => setPassword(event.target.value)}
            data-cy="clinical-password"
          />
        </label>
        <ErrorMessage message={error} />
        <button
          type="submit"
          disabled={busy}
          data-cy="clinical-login-submit"
        >
          {busy ? '正在登录…' : '登录'}
        </button>
      </form>
    </main>
  );
}
function DetailsDialog({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement | null>(null);
  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => element?.close();
  }, []);
  return (
    <dialog
      ref={dialog}
      className="clinical-detail-overlay"
      aria-label="检查详情"
      onClose={onClose}
      data-cy="clinical-details"
    >
      {children}
    </dialog>
  );
}

export function WorklistPage() {
  const navigate = useNavigate();
  const session = useSyncExternalStore(clinicalSession.subscribe, clinicalSession.getSnapshot);
  const [draft, setDraft] = useState<Filters>(() => getWorklistState().draft);
  const [filters, setFilters] = useState<Filters>(() => getWorklistState().filters);
  const [page, setPage] = useState(() => getWorklistState().page);
  const [retry, setRetry] = useState(0);
  const queryKey = JSON.stringify([filters, page, retry]);
  const [response, setResponse] = useState({
    key: '',
    value: { studies: [], total: 0 } as StudyPage,
    error: '',
  });
  const busy = response.key !== queryKey;
  const result = busy ? { studies: [], total: 0 } : response.value;
  const queryError = busy ? '' : response.error;
  const [opening, setOpening] = useState('');
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Study | null>(null);
  const [tab, setTab] = useState('report');
  const openRequest = useRef<AbortController | null>(null);
  useEffect(() => {
    saveWorklistState({ draft, filters, page });
  }, [draft, filters, page]);
  useEffect(() => () => openRequest.current?.abort(), []);
  useEffect(() => {
    const controller = new AbortController();
    clinicalApi
      .studies(filters, page, controller.signal)
      .then(value => {
        if (!controller.signal.aborted) {
          setResponse({ key: queryKey, value, error: '' });
        }
      })
      .catch(error => {
        if (!controller.signal.aborted) {
          setResponse({ key: queryKey, value: { studies: [], total: 0 }, error: errorText(error) });
        }
      });
    return () => controller.abort();
  }, [filters, page, queryKey]);
  async function open(study: Study) {
    openRequest.current?.abort();
    const controller = new AbortController();
    openRequest.current = controller;
    setOpening(study.register_key);
    setError('');
    try {
      const uids = await clinicalApi.uids(study, controller.signal);
      if (!controller.signal.aborted) {
        navigate(viewerURL(study, uids));
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        setError(errorText(error));
      }
    }
    if (!controller.signal.aborted) {
      setOpening('');
    }
  }
  const fields = [
    ['chinese_name', '姓名', 'text'],
    ['patient_id', '患者 ID', 'text'],
    ['study_id', '检查号', 'text'],
    ['register_time_begin', '开始日期', 'date'],
    ['register_time_end', '结束日期', 'date'],
  ];
  function search(event: FormEvent) {
    event.preventDefault();
    if (
      draft.register_time_begin &&
      draft.register_time_end &&
      draft.register_time_begin > draft.register_time_end
    ) {
      setError('开始日期不能晚于结束日期。');
      return;
    }
    setPage(1);
    setFilters({ ...draft });
    setError('');
    setSelected(null);
  }
  return (
    <main
      className="clinical-worklist"
      data-cy="clinical-worklist"
    >
      <header>
        <div>
          <h1>临床影像工作站</h1>
          <span>检查查询</span>
        </div>
        <div>
          <span>{session?.username}</span>
          <button
            onClick={clinicalSession.clearToken}
            data-cy="clinical-logout"
          >
            退出登录
          </button>
        </div>
      </header>
      <form
        onSubmit={search}
        className="clinical-filters"
      >
        {fields.map(([key, label, type]) => (
          <label key={key}>
            {label}
            <input
              type={type}
              value={draft[key] || ''}
              onChange={event => setDraft({ ...draft, [key]: event.target.value })}
              data-cy={`clinical-filter-${key}`}
            />
          </label>
        ))}
        <label>
          检查类型
          <select
            value={draft.modality_code || ''}
            onChange={event => setDraft({ ...draft, modality_code: event.target.value })}
          >
            <option value="">全部</option>
            {['CT', 'MR', 'DX', 'CR', 'US', 'MG', 'PT', 'XA'].map(value => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          disabled={busy}
          data-cy="clinical-search"
        >
          查询
        </button>
        <button
          type="button"
          onClick={() => {
            setDraft({});
            setFilters({});
            setPage(1);
            setError('');
            setSelected(null);
          }}
        >
          重置
        </button>
      </form>
      <ErrorMessage message={error || queryError} />
      {(error || queryError) && (
        <button
          onClick={() => {
            setRetry(retry + 1);
            setError('');
            setSelected(null);
          }}
        >
          重试查询
        </button>
      )}
      {busy && <p role="status">正在查询检查…</p>}
      {!busy && !error && !queryError && !result.studies.length && (
        <p data-cy="clinical-empty">未查询到检查记录</p>
      )}
      <div className="clinical-table-wrap">
        <table>
          <thead>
            <tr>
              {[
                '姓名',
                '患者 ID',
                '性别/年龄',
                '检查号',
                '类型/部位',
                '检查时间',
                '报告状态',
                '操作',
              ].map(label => (
                <th key={label}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.studies.map(study => (
              <tr
                key={study.register_key}
                data-cy="clinical-study-row"
              >
                <td>{study.chinese_name || '—'}</td>
                <td>{study.patient_id || '—'}</td>
                <td>
                  {study.sex} / {study.age}
                </td>
                <td>{study.study_id || '—'}</td>
                <td>
                  {study.modality_code} {study.bodypart_name}
                </td>
                <td>{study.study_time || study.register_time || '—'}</td>
                <td>{statusText(study.state)}</td>
                <td>
                  <button
                    disabled={!!opening}
                    onClick={() => open(study)}
                    data-cy="clinical-open-image"
                  >
                    {opening === study.register_key ? '正在打开…' : '影像'}
                  </button>
                  <button
                    onClick={() => {
                      setSelected(study);
                      setTab('report');
                    }}
                    data-cy="clinical-open-report"
                  >
                    报告
                  </button>
                  <button
                    onClick={() => {
                      setSelected(study);
                      setTab('history');
                    }}
                    data-cy="clinical-open-history"
                  >
                    历史
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        total={result.total}
        busy={busy}
        onPage={next => {
          setPage(next);
          setError('');
          setSelected(null);
        }}
      />
      {selected && (
        <DetailsDialog onClose={() => setSelected(null)}>
          <header>
            <h2>
              {selected.chinese_name || '检查详情'} · {selected.modality_code}{' '}
              {selected.bodypart_name}
            </h2>
            <button
              onClick={() => setSelected(null)}
              data-cy="clinical-close-details"
            >
              关闭
            </button>
          </header>
          <div
            role="tablist"
            className="clinical-tabs"
            aria-label="检查资料"
          >
            <button
              role="tab"
              aria-selected={tab === 'report'}
              onClick={() => setTab('report')}
            >
              报告
            </button>
            <button
              role="tab"
              aria-selected={tab === 'history'}
              onClick={() => setTab('history')}
            >
              历史检查
            </button>
          </div>
          <div role="tabpanel">
            {tab === 'report' ? (
              <ReportView registerKey={selected.register_key} />
            ) : (
              <HistoryView study={selected} />
            )}
          </div>
        </DetailsDialog>
      )}
    </main>
  );
}
